/* =====================================================================
 * Usage dashboard API client.
 *
 * All endpoints are scoped server-side to the calling user — there is
 * deliberately no `user_id` parameter to pass. See
 * `backend/app/domains/workspace/dashboard_router.py`.
 * ===================================================================== */

import { authFetch, getHeaders, API_BASE_URL } from './authFetch';

const DASHBOARD_PREFIX = `${API_BASE_URL}/api/v1/dashboard`;

export type DashboardGranularity = 'day' | 'week' | 'month';

/** Lifecycle of one agent turn. */
export type AgentRunStatus =
  | 'queued'
  | 'running'
  | 'done'
  | 'failed'
  | 'cancelled'
  | 'expired';

export interface AgentRun {
  run_id: string;
  user_id: string;
  workspace_type: string;
  session_id: string | null;
  item_id: string | null;
  /** Current turn's user question, collapsed to one display line. */
  query_preview: string | null;
  /** Conversation/document title used only as secondary navigation context. */
  item_title: string | null;
  /** Backward-compatible display fallback: query_preview, then item_title. */
  title: string | null;
  status: AgentRunStatus;
  enqueued_at: string;
  started_at: string | null;
  finished_at: string | null;
  wait_ms: number;
  duration_ms: number;
  /** Nullable because runs recorded before detailed usage capture are unknown. */
  input_token_count?: number | null;
  output_token_count?: number | null;
  /** Subset of output_token_count; do not add it to total_token_count again. */
  reasoning_token_count?: number | null;
  total_token_count?: number | null;
  error_code: string | null;
}

export interface DashboardSummary {
  window_days: number;
  total_runs: number;
  /** Null means every run in the window predates token capture. */
  total_token_count?: number | null;
  token_run_count?: number;
  unknown_token_run_count?: number;
  avg_duration_ms: number;
  by_workspace: Record<string, number>;
  active_count: number;
  running_count: number;
  queued_count: number;
}

export interface HeatmapCell {
  date: string;
  count: number;
}

export interface DashboardHeatmap {
  start: string;
  end: string;
  /** Busiest day in the window — the ramp is scaled against this. */
  peak: number;
  total: number;
  /** One entry per day in the window, zeros included. */
  cells: HeatmapCell[];
}

export interface SeriesPoint {
  bucket: string;
  total: number;
  by_workspace: Record<string, number>;
  total_token_count?: number | null;
  tokens_by_workspace?: Record<string, number>;
  token_run_count?: number;
  unknown_token_run_count?: number;
}

export interface DashboardSeries {
  granularity: DashboardGranularity;
  start: string;
  end: string;
  workspaces: string[];
  token_run_count?: number;
  unknown_token_run_count?: number;
  points: SeriesPoint[];
}

export interface DashboardDateRange {
  /** Inclusive UTC calendar date (YYYY-MM-DD). */
  start: string;
  /** Inclusive UTC calendar date (YYYY-MM-DD). */
  end: string;
}

export interface DashboardRecentPage {
  runs: AgentRun[];
  total: number;
  limit: number;
  offset: number;
}

/** Thrown when the tenant has no history storage wired (HTTP 503). */
export class DashboardUnavailableError extends Error {
  constructor() {
    super('dashboard_unavailable');
    this.name = 'DashboardUnavailableError';
  }
}

async function get<T>(path: string, params?: Record<string, string | number>): Promise<T> {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    qs.set(key, String(value));
  }
  const query = qs.toString();
  const res = await authFetch(`${DASHBOARD_PREFIX}${path}${query ? `?${query}` : ''}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  if (res.status === 503) {
    // Distinguished from a network failure so the page can say
    // "unavailable" rather than rendering an empty state that reads as
    // "you have never used this".
    throw new DashboardUnavailableError();
  }
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  }
  return (await res.json()) as T;
}

export function fetchDashboardSummary(days = 30): Promise<DashboardSummary> {
  return get<DashboardSummary>('/summary', { days });
}

export function fetchDashboardHeatmap(days = 365): Promise<DashboardHeatmap> {
  return get<DashboardHeatmap>('/heatmap', { days });
}

export function fetchDashboardSeries(
  granularity: DashboardGranularity = 'day',
  days = 30,
  range?: DashboardDateRange,
): Promise<DashboardSeries> {
  return get<DashboardSeries>(
    '/series',
    range
      ? { granularity, start: range.start, end: range.end }
      : { granularity, days },
  );
}

export function fetchDashboardRecent(
  limit = 20,
  offset = 0,
): Promise<DashboardRecentPage> {
  return get<DashboardRecentPage>('/recent', { limit, offset });
}

export function fetchDashboardActive(): Promise<{ runs: AgentRun[] }> {
  return get<{ runs: AgentRun[] }>('/active');
}
